import {
  getZuuFnaQuestionAliases,
  getZuuFnaQuestionValueFromList,
  toZuuFnaStoredQuestionValue,
  type FnaQuestionType,
} from "./zuuFnaFieldMap.js";

function getSelectedKeys(question: any): string[] {
  return (question?.items || [])
    .filter((item: any) => item?.value === true)
    .map((item: any) => item?.key)
    .filter((key: any): key is string => Boolean(key));
}

function buildUpdatedItems(
  items: any[] | undefined,
  value: any,
  type: FnaQuestionType,
) {
  let nextItems = Array.isArray(items) ? [...items] : [];

  if (type === "INPUT") {
    const itemIndex = nextItems.findIndex((item) => item?.key === "A");
    if (itemIndex >= 0) {
      nextItems[itemIndex] = { ...nextItems[itemIndex], value };
    } else {
      nextItems.push({ key: "A", value });
    }
    return nextItems;
  }

  if (type === "RADIO") {
    let found = false;
    nextItems = nextItems.map((item) => {
      if (item?.key === value) {
        found = true;
        return { ...item, value: true };
      }
      return { ...item, value: false };
    });
    if (!found && value) {
      nextItems.push({ key: value, value: true });
    }
    return nextItems;
  }

  const values = Array.isArray(value) ? value : [];
  nextItems = nextItems.map((item) => ({
    ...item,
    value: values.includes(item?.key),
  }));
  values.forEach((selectedKey) => {
    if (!nextItems.find((item) => item?.key === selectedKey)) {
      nextItems.push({ key: selectedKey, value: true });
    }
  });
  return nextItems;
}

export function getLegacyQuestionValueFromList(
  list: any[] | undefined,
  questionKey: string,
  type: FnaQuestionType = "RADIO",
) {
  const question = list?.find((item) => item?.question === questionKey);
  if (!question) {
    return undefined;
  }

  if (type === "INPUT") {
    return question.items?.find((item: any) => item?.key === "A")?.value;
  }

  const selectedKeys = getSelectedKeys(question);
  if (type === "CHECKBOX") {
    return selectedKeys;
  }

  return selectedKeys[0];
}

export function updateLegacyQuestionList(
  list: any[] | undefined,
  questionKey: string,
  value: any,
  type: FnaQuestionType = "RADIO",
) {
  const nextList = (list || []).filter((question) => question?.question);
  const questionIndex = nextList.findIndex(
    (question) => question.question === questionKey,
  );
  const currentQuestion = questionIndex >= 0 ? nextList[questionIndex] : undefined;
  const nextQuestion = {
    ...currentQuestion,
    question: questionKey,
    type,
    items: buildUpdatedItems(currentQuestion?.items, value, type),
  };

  if (questionIndex >= 0) {
    nextList[questionIndex] = nextQuestion;
  } else {
    nextList.push(nextQuestion);
  }

  return nextList;
}

export function getQuestionValueFromListByVariant(
  list: any[] | undefined,
  questionKey: string,
  type: FnaQuestionType = "RADIO",
  options?: { isZuuMode?: boolean },
) {
  if (options?.isZuuMode) {
    return getZuuFnaQuestionValueFromList(list || [], questionKey, type);
  }
  return getLegacyQuestionValueFromList(list, questionKey, type);
}

export function updateQuestionListByVariant(
  list: any[] | undefined,
  questionKey: string,
  value: any,
  type: FnaQuestionType = "RADIO",
  options?: { isZuuMode?: boolean },
) {
  if (!options?.isZuuMode) {
    return updateLegacyQuestionList(list, questionKey, value, type);
  }

  const nextList = (list || []).filter((question) => question?.question);
  const { canonicalKey, storedType, storedValue } = toZuuFnaStoredQuestionValue(
    questionKey,
    value,
    type,
  );
  const aliases = new Set(getZuuFnaQuestionAliases(questionKey));
  const questionIndex = nextList.findIndex((question) =>
    aliases.has(question.question),
  );
  const currentQuestion = questionIndex >= 0 ? nextList[questionIndex] : undefined;
  const nextQuestion = {
    ...currentQuestion,
    question: canonicalKey,
    type: storedType,
    items: buildUpdatedItems(currentQuestion?.items, storedValue, storedType),
  };

  if (questionIndex >= 0) {
    nextList[questionIndex] = nextQuestion;
  } else {
    nextList.push(nextQuestion);
  }

  return nextList;
}
